import { Card } from '@/components/ui/Card';
import {
  arcMidpointRad,
  describeDonutArcRad,
  semiDonutSplitRad,
} from '@/components/snapshot/snapshotCharts';
import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import {
  formatSnapshotCurrency,
  formatSnapshotNumber,
  organicVsPaidSplit,
  type OrganicPaidBuckets,
} from '@/api/snapshotMetrics';

type GaugeMetric = keyof Pick<
  OrganicPaidBuckets['organic'],
  'sales30d' | 'purchases30d' | 'clicks'
>;
type GaugeVariant = 'sales' | 'orders' | 'clicks';

const METRIC_FORMAT: Record<
  GaugeMetric,
  { format: (v: number) => string; polarity: 'higher_is_better' | 'lower_is_better' }
> = {
  sales30d: { format: formatSnapshotCurrency, polarity: 'higher_is_better' },
  purchases30d: { format: (v) => formatSnapshotNumber(v, 0), polarity: 'higher_is_better' },
  clicks: { format: (v) => formatSnapshotNumber(v, 0), polarity: 'higher_is_better' },
};

/** Category colors are fixed across all gauge cards (not per-metric variants). */
const CATEGORY_THEME = {
  organic: { fill: '#41724f', label: 'text-success' },
  paid: { fill: '#5980a6', label: 'text-brand' },
} as const;

/** Previous-period ring: lighter shades of the same category colors. */
const INNER_ORGANIC_COLOR = '#6b9a78';
const INNER_PAID_COLOR = '#749dc4';

/** Compact arc label — decimals crowd thin bands and hurt legibility. */
function formatArcPercent(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${Math.round(value)}%`;
}

function relativeLuminance(hex: string): number {
  const raw = hex.replace('#', '');
  if (raw.length !== 6) return 0.5;
  const channel = (start: number) => {
    const c = parseInt(raw.slice(start, start + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/** Pick fill + halo stroke so labels stay readable on light and dark segments. */
function labelColorsForBg(bgHex: string): { fill: string; stroke: string } {
  if (relativeLuminance(bgHex) > 0.45) {
    return { fill: '#0f172a', stroke: 'rgba(255,255,255,0.9)' };
  }
  return { fill: '#ffffff', stroke: 'rgba(15,23,42,0.45)' };
}

function SegmentLabel({
  x,
  y,
  text,
  bg,
  size,
}: {
  x: number;
  y: number;
  text: string;
  bg: string;
  size: number;
}) {
  const { fill, stroke } = labelColorsForBg(bg);
  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fill={fill}
      stroke={stroke}
      strokeWidth={size >= 13 ? 3.25 : 2.75}
      paintOrder="stroke fill"
      strokeLinejoin="round"
      fontSize={size}
      fontWeight={700}
      style={{ letterSpacing: '-0.02em' }}
    >
      {text}
    </text>
  );
}

function NestedSemiGauge({
  spPct,
  sbSdPct,
  prevSpPct,
  prevSbSdPct,
  spColor,
  sbSdColor,
  showPrevCaption,
  hideInnerPercentLabels,
}: {
  spPct: number;
  sbSdPct: number;
  prevSpPct: number;
  prevSbSdPct: number;
  spColor: string;
  sbSdColor: string;
  showPrevCaption: boolean;
  hideInnerPercentLabels: boolean;
}) {
  const cx = 140;
  /** Flat baseline of the arch (controls vertical placement inside the SVG). */
  const cy = 100;
  // Thicker bands so percentage labels have room to breathe.
  const outerInner = 72;
  const outerOuter = 112;
  const innerInner = 46;
  const innerOuter = 66;
  const left = Math.PI;
  const right = 0;

  const split = semiDonutSplitRad(spPct);
  const prevSplit = semiDonutSplitRad(prevSpPct);

  const outerMidR = (outerInner + outerOuter) / 2;
  const innerMidR = (innerInner + innerOuter) / 2;

  const outerSpLabel = arcMidpointRad(cx, cy, outerMidR, left, split);
  const outerSbSdLabel = arcMidpointRad(cx, cy, outerMidR, split, right);
  const innerSpLabel = arcMidpointRad(cx, cy, innerMidR, left, prevSplit);
  const innerSbSdLabel = arcMidpointRad(cx, cy, innerMidR, prevSplit, right);

  // Slightly higher threshold so tiny slices don't show unreadable labels.
  const showOuterSpLabel = spPct >= 10;
  const showOuterSbSdLabel = sbSdPct >= 10;
  const showInnerSpLabel = !hideInnerPercentLabels && prevSpPct >= 12;
  const showInnerSbSdLabel = !hideInnerPercentLabels && prevSbSdPct >= 12;

  return (
    <div className="relative mx-auto w-full max-w-[300px] py-2">
      <svg
        viewBox="0 0 280 118"
        width="100%"
        style={{ height: 175 }}
        preserveAspectRatio="xMidYMid meet"
        className="block overflow-visible"
        aria-hidden
      >
        <g>
          <path
            d={describeDonutArcRad(cx, cy, outerInner, outerOuter, left, split)}
            fill={spColor}
          />
          <path
            d={describeDonutArcRad(cx, cy, outerInner, outerOuter, split, right)}
            fill={sbSdColor}
          />
          <path
            d={describeDonutArcRad(cx, cy, innerInner, innerOuter, left, prevSplit)}
            fill={INNER_ORGANIC_COLOR}
          />
          <path
            d={describeDonutArcRad(cx, cy, innerInner, innerOuter, prevSplit, right)}
            fill={INNER_PAID_COLOR}
          />
          {showOuterSpLabel ? (
            <SegmentLabel
              x={outerSpLabel.x}
              y={outerSpLabel.y}
              text={formatArcPercent(spPct)}
              bg={spColor}
              size={14}
            />
          ) : null}
          {showOuterSbSdLabel ? (
            <SegmentLabel
              x={outerSbSdLabel.x}
              y={outerSbSdLabel.y}
              text={formatArcPercent(sbSdPct)}
              bg={sbSdColor}
              size={14}
            />
          ) : null}
          {showInnerSpLabel ? (
            <SegmentLabel
              x={innerSpLabel.x}
              y={innerSpLabel.y}
              text={formatArcPercent(prevSpPct)}
              bg={INNER_ORGANIC_COLOR}
              size={12}
            />
          ) : null}
          {showInnerSbSdLabel ? (
            <SegmentLabel
              x={innerSbSdLabel.x}
              y={innerSbSdLabel.y}
              text={formatArcPercent(prevSbSdPct)}
              bg={INNER_PAID_COLOR}
              size={12}
            />
          ) : null}
        </g>
      </svg>
      {showPrevCaption ? (
        <p className="text-center text-[10px] font-medium text-foreground-muted">
          Inner ring: previous period
        </p>
      ) : null}
    </div>
  );
}

export function SnapshotGaugeCard({
  title,
  metric,
  variant: _variant,
  currentBuckets,
  previousBuckets,
  currentTotal,
  previousTotal,
  compareEnabled,
}: {
  title: string;
  metric: GaugeMetric;
  variant: GaugeVariant;
  currentBuckets: OrganicPaidBuckets;
  previousBuckets: OrganicPaidBuckets;
  currentTotal: number;
  previousTotal: number;
  compareEnabled: boolean;
}) {
  const { format, polarity } = METRIC_FORMAT[metric];
  const current = organicVsPaidSplit(currentBuckets, metric, currentTotal);
  const previous = organicVsPaidSplit(previousBuckets, metric, previousTotal);

  return (
    <Card className="flex flex-col p-5 shadow-sm">
      <p className="text-sm font-medium text-foreground-muted">{title}</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <p className="text-[1.75rem] font-bold leading-tight text-foreground">
          {format(currentTotal)}
        </p>
        {compareEnabled ? (
          <SnapshotChangePill
            current={currentTotal}
            previous={previousTotal}
            polarity={polarity}
            show
          />
        ) : null}
      </div>
      {compareEnabled ? (
        <p className="mt-1 text-xs text-foreground-muted">Prev: {format(previousTotal)}</p>
      ) : null}

      <div className="my-2 flex shrink-0 items-center justify-center py-2">
        <NestedSemiGauge
          spPct={current.organicPct}
          sbSdPct={current.paidPct}
          prevSpPct={previous.organicPct}
          prevSbSdPct={previous.paidPct}
          spColor={CATEGORY_THEME.organic.fill}
          sbSdColor={CATEGORY_THEME.paid.fill}
          showPrevCaption={compareEnabled}
          hideInnerPercentLabels={compareEnabled && current.organicPct < 1}
        />
      </div>

      <div className="mt-auto grid grid-cols-2 gap-6 border-t border-border/60 pt-4">
        <div>
          <p className={`text-sm font-semibold ${CATEGORY_THEME.organic.label}`}>Organic</p>
          <p className="mt-1 text-lg font-bold text-foreground">{format(current.organic)}</p>
          {compareEnabled ? (
            <div className="mt-1">
              <SnapshotChangePill
                current={current.organic}
                previous={previous.organic}
                polarity={polarity}
                show
              />
            </div>
          ) : null}
        </div>
        <div>
          <p className={`text-sm font-semibold ${CATEGORY_THEME.paid.label}`}>Paid</p>
          <p className="mt-1 text-lg font-bold text-foreground">{format(current.paid)}</p>
          {compareEnabled ? (
            <div className="mt-1">
              <SnapshotChangePill
                current={current.paid}
                previous={previous.paid}
                polarity={polarity}
                show
              />
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
